import {getTimezoneOffset} from '@wix/panda-js-utils'
import {WEEKS_PER_CALENDAR_MONTH, WEEK_LENGTH, WidgetDates} from '@wix/wix-events-commons-statics'
import {IStyle} from '@wix/yoshi-flow-editor'
import {isEmpty} from 'lodash'
import {getLocale, getTimezone} from '../../../../commons/selectors/environment'
import type {AppProps} from '../components/app/interfaces'
import {CalendarLayout, State} from '../types/state'
import {getEventById, shouldShowDemoEvents, shouldShowRealEvents} from './events'
import {getCalendarWeekStartDay} from './list-settings'

export const getEventDetailsStyleHash = (context: Pick<AppProps, 'host'>) => {
  const eventDetailsSettings = {
    booleans: [
      'listShowImage',
      'listExtraInfoShowDate',
      'listExtraInfoShowLocation',
      'listShowDescription',
      'listShowSocialShare',
    ],
    numbers: [
      'calendarDetailsTitleTextSize',
      'calendarDetailsDateLocationTextSize',
      'calendarDetailsDescriptionTextSize',
      'multiImageRatio',
      'calendarWeekStartDay',
    ],
  }

  const style: IStyle = context.host.style

  const settings = Object.entries(style.styleParams).reduce((result, [type, params]) => {
    if (eventDetailsSettings[type]) {
      result[type] = Object.entries(params).reduce((typeResult, [key, value]) => {
        if (eventDetailsSettings[type].includes(key)) {
          typeResult[key] = value
        }

        return typeResult
      }, {})
    }

    return result
  }, {})

  return JSON.stringify(settings)
}

export const isCalendarPageLoaded = ({loadedPages}: CalendarLayout, referenceMonth: string) =>
  loadedPages.includes(referenceMonth)

export const isCalendarPageLoading = ({loading}: CalendarLayout) => !!loading

export const getCalendarLoadingState = (state: State) => state.calendarLayout.loading

export const getWeekdaysFromStartWeekday = (state: State) => {
  const weekDayNames = state.calendarLayout.weekDayNames
  const startWeekDay = getCalendarWeekStartDay(state)

  return [...weekDayNames.slice(startWeekDay), ...weekDayNames.slice(0, startWeekDay)]
}

export const getWeekdayName = (state: State, calendarColumnIndex: number, getWeekdayLabel: Function) => {
  const startWeekDay = getCalendarWeekStartDay(state)
  const locale = getLocale(state)
  const weekDayIndex = (calendarColumnIndex + startWeekDay) % WEEK_LENGTH

  return getWeekdayLabel(weekDayIndex, locale)
}

export const getDaysInGridMonth = (state: State, referenceMonth: string) => {
  const startWeekDay = getCalendarWeekStartDay(state)
  const startOfMonthWeekday = state.calendarLayout.days[referenceMonth].weekDay
  let showPastMonthDaysCount = startOfMonthWeekday - startWeekDay
  if (showPastMonthDaysCount < 0) {
    showPastMonthDaysCount += WEEK_LENGTH
  }
  const allDays = Object.keys(state.calendarLayout.days).sort()
  const firstDayIndex = allDays.indexOf(referenceMonth) - showPastMonthDaysCount

  return allDays.slice(firstDayIndex, firstDayIndex + WEEKS_PER_CALENDAR_MONTH * WEEK_LENGTH)
}

export const getDaysInGrid = (state: State, monthly: boolean) => {
  const referenceDate = monthly ? getReferenceMonth(state) : getReferenceDay(state)

  const startWeekDay = getCalendarWeekStartDay(state)
  const startOfPeriodWeekday = state.calendarLayout.days[referenceDate].weekDay
  let showPastPeriodDaysCount = startOfPeriodWeekday - startWeekDay

  if (showPastPeriodDaysCount < 0) {
    showPastPeriodDaysCount += WEEK_LENGTH
  }

  const allDays = Object.keys(state.calendarLayout.days).sort()
  const firstDayIndex = allDays.indexOf(referenceDate) - showPastPeriodDaysCount

  const endIndex = monthly ? firstDayIndex + WEEKS_PER_CALENDAR_MONTH * WEEK_LENGTH : firstDayIndex + WEEK_LENGTH

  return allDays.slice(firstDayIndex, endIndex)
}

export const getDaysInMonth = (state: State, referenceMonth: string) =>
  Object.keys(state.calendarLayout.days)
    .filter(day => state.calendarLayout.days[day].month === referenceMonth)
    .sort()

export const getDaysInMonthWithEvents = (state: State, referenceMonth: string) =>
  getDaysInMonth(state, referenceMonth).filter(day => !!getCalendarEvents(state)[day])

export const getGridCellLocation = (state: State, day: string) => {
  const days = getDaysInGridMonth(state, getReferenceMonth(state))
  const cellIndex = days.indexOf(day)
  const weekIndex = Math.floor(cellIndex / WEEK_LENGTH)
  const weekDayIndex = cellIndex % WEEK_LENGTH
  return {weekDayIndex, weekIndex}
}

export const getCalendarEvents = (state: State): Dictionary<string[]> => {
  if (shouldShowRealEvents(state)) {
    return state.calendarLayout.events || {}
  } else if (shouldShowDemoEvents(state)) {
    return state.calendarLayout.demoEvents || {}
  }
  return {}
}

export const getEventsForDay = (state: State, day: string) => {
  const eventIds = getCalendarEvents(state)[day] ?? []
  return eventIds.map(eventId => getEventById(state, eventId)).filter(Boolean)
}

export const isDayInCurrentMonth = (state: State, day: string) =>
  state.calendarLayout.days[day]?.month === getReferenceMonth(state)

export const getReferenceMonth = (state: State) => state.calendarLayout.referenceMonth

export const getFullReferenceMonthName = (state: State, getFullMonthText: Function) => {
  const referenceMonth = getReferenceMonth(state)
  const selectedDateInTimezone = getDateInTimezone(state, referenceMonth)
  const locale = getLocale(state)

  return getFullMonthText(selectedDateInTimezone, locale)
}

export const getReferenceDay = (state: State) => state.calendarLayout.referenceDay

export const getReferencedMonthName = (state: State) =>
  state.calendarLayout.month[getReferenceMonth(state)].fullMonthText

export const getMonthOfDate = (state: State, day: string) => state.calendarLayout.days[day]?.month

const getStartOfDay = (date: Date, timezone: string, dayOfMonth: number) => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const startOfDay = new Date(Date.UTC(year, month, dayOfMonth))

  return new Date(startOfDay.getTime() - getTimezoneOffset(timezone, startOfDay)).toISOString()
}

export const getDateInTimezone = (state: State, date: string) => {
  const timezone = getTimezone(state)
  const inputDate = new Date(date)
  const dateInTimezone = inputDate.toLocaleDateString('en-US', {timeZone: timezone})

  return new Date(dateInTimezone)
}

export const getReferenceDayForSelectedDate = (date: Date, timezone: string) => {
  return getStartOfDay(date, timezone, date.getDate())
}

export const getReferenceMonthForSelectedDate = (date: Date, timezone: string) => {
  return getStartOfDay(date, timezone, 1)
}

export const getNextMonth = (state: State) => state.calendarLayout.month[getReferenceMonth(state)].next
export const getPreviousMonth = (state: State) => state.calendarLayout.month[getReferenceMonth(state)].previous
export const getToday = (state: State) => state.calendarLayout.today

export const getDayMonthText = (state: State, day: string) => state.calendarLayout.days[day].dayMonthText
export const getDayNumber = (state: State, day: string) => state.calendarLayout.days[day].dayNumber

export const getSelectedDate = (state: State) => state.calendarLayout.monthly?.selectedDate
export const getSelectedEventId = (state: State) => state.calendarLayout.monthly?.selectedEventId

export const isAnyPageLoaded = (state: State) => {
  const referenceMonth = getReferenceMonth(state)
  return isCalendarPageLoaded(state.calendarLayout, referenceMonth)
}

export const isDayWeekend = (state: State, day: string) => {
  const weekDay = state.calendarLayout.days[day].weekDay

  return weekDay === 0 || weekDay === 6
}

export const getDefaultReferenceDay = (showcase: boolean, dates: WidgetDates) => {
  if (!showcase || !dates.calendar?.referenceDate || !dates.calendar?.events || !dates.calendar?.days) {
    return dates.calendar?.today
  }

  const referenceMonth = dates.calendar?.referenceDate

  const eventDays = isEmpty(dates.calendar?.events)
    ? dates.calendar?.demoEvents
      ? Object.keys(dates.calendar?.demoEvents)
      : []
    : Object.keys(dates.calendar?.events)

  const allDays = Object.keys(dates.calendar?.days)
  const startOfMonthIndex = allDays.indexOf(referenceMonth)

  if (startOfMonthIndex === -1) {
    return dates.calendar?.today
  }

  const firstEventOfMonthDay = allDays.slice(startOfMonthIndex).find(day => eventDays.includes(day))

  return firstEventOfMonthDay || dates.calendar?.today
}
